import * as React from 'react';
import { useState } from 'react';
import { Box, Button, Alert } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { withTranslation } from 'react-i18next';
import DataTable from '../../shared/DataTable';
import { useAccountingInvoiceDetail, downloadInvoiceSummaryCsv } from '../hooks/useReportsApi';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

// Format currency but return empty string for zero values (keeps table clean)
const formatCurrencyOrEmpty = (value) => {
  const num = parseFloat(value);
  if (!num || num === 0) return '';
  return formatCurrency(num);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

function InvoiceDetailSection({ t, year, month }) {
  const { data, loading, error } = useAccountingInvoiceDetail(year, month);
  const [csvLoading, setCsvLoading] = useState(false);

  const handleDownloadCsv = async () => {
    setCsvLoading(true);
    try {
      await downloadInvoiceSummaryCsv(year, month);
    } catch (err) {
      console.error('CSV download failed:', err);
    } finally {
      setCsvLoading(false);
    }
  };

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  // Column visibility by breakpoint:
  // xs (phone):  Pvm, Vastaanottaja, Maksutapa, Yhteensä
  // sm (tablet): + Laskun nro, Y-tunnus, ALV
  // md:          + all VAT rate columns
  // lg:          + Maksupäivä
  const columns = [
    {
      key: 'invoice_date',
      label: t('reports.accounting.invoiceDate'),
      width: '1%',
      noWrap: true,
      render: (row) => formatDate(row.invoice_date),
    },
    {
      key: 'invoice_number',
      label: t('reports.accounting.invoiceNumber'),
      width: '1%',
      noWrap: true,
      hideBelow: 'sm',
      render: (row) => row.invoice_number,
    },
    {
      key: 'customer_name',
      label: t('reports.accounting.recipient'),
      maxWidth: 250,
      render: (row) => row.customer_name,
    },
    {
      key: 'customer_business_id',
      label: t('reports.accounting.businessIdColumn'),
      width: '1%',
      noWrap: true,
      hideBelow: 'sm',
      render: (row) => row.customer_business_id || '-',
    },
    {
      key: 'payment_method',
      label: t('reports.accounting.paymentMethodColumn'),
      width: '1%',
      noWrap: true,
      render: (row) => row.payment_method_display,
    },
    {
      key: 'paid_date',
      label: t('reports.accounting.paidDate'),
      width: '1%',
      noWrap: true,
      hideBelow: 'lg',
      render: (row) => row.paid_date ? formatDate(row.paid_date) : '-',
    },
    {
      key: 'sales_25_5',
      label: '25,5%',
      align: 'right',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatCurrencyOrEmpty(row.sales_25_5),
    },
    {
      key: 'sales_24',
      label: '24%',
      align: 'right',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatCurrencyOrEmpty(row.sales_24),
    },
    {
      key: 'sales_13_5',
      label: '13,5%',
      align: 'right',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatCurrencyOrEmpty(row.sales_13_5),
    },
    {
      key: 'sales_10',
      label: '10%',
      align: 'right',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatCurrencyOrEmpty(row.sales_10),
    },
    {
      key: 'sales_0',
      label: '0%',
      align: 'right',
      noWrap: true,
      hideBelow: 'md',
      render: (row) => formatCurrencyOrEmpty(row.sales_0),
    },
    {
      key: 'total_vat',
      label: t('reports.accounting.vatColumn'),
      align: 'right',
      noWrap: true,
      hideBelow: 'sm',
      render: (row) => formatCurrency(row.total_vat),
    },
    {
      key: 'total_incl_vat',
      label: t('reports.accounting.inclVatColumn'),
      align: 'right',
      noWrap: true,
      render: (row) => formatCurrency(row.total_incl_vat),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={csvLoading}
        >
          {t('reports.accounting.downloadCsv')}
        </Button>
      </Box>

      <DataTable
        columns={columns}
        data={data?.invoices || []}
        loading={loading}
        alwaysVisible
        horizontalScroll
        emptyIcon={<ReceiptLongIcon />}
        emptyTitle={t('reports.accounting.noInvoices')}
        emptyDescription={t('reports.accounting.noInvoicesDescription')}
      />
    </Box>
  );
}

export default withTranslation()(InvoiceDetailSection);
